import { db } from "../../../firebase";
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  deleteDoc,
} from "firebase/firestore";
import { FORM_STATUS } from "../models/types";

/**
 * Response Service
 * Handles all interactions with Firebase for questionnaire responses
 */
const responseService = {
  /**
   * Start a new questionnaire response
   * @param {string} questionnaireId - The questionnaire ID
   * @param {string} userId - The user ID
   * @param {string} projectId - The project ID
   * @param {Object} questionnaireSnapshot - The full questionnaire structure at the time of response creation
   * @returns {Promise<string>} - The ID of the created response
   */
  async startQuestionnaireResponse(
    questionnaireId,
    userId,
    projectId,
    questionnaireSnapshot
  ) {
    try {
      const response = {
        questionnaireId,
        userId,
        projectId,
        responses: {},
        startedAt: serverTimestamp(),
        submittedAt: null,
        status: FORM_STATUS.IN_PROGRESS,
        questionnaireSnapshot: questionnaireSnapshot || null,
      };

      const docRef = await addDoc(
        collection(db, "questionnaireResponses"),
        response
      );
      return docRef.id;
    } catch (error) {
      console.error("Error starting questionnaire response:", error);
      throw error;
    }
  },

  /**
   * Get a response by ID
   * @param {string} id - The response ID
   * @returns {Promise<Object>} - The response data
   */
  async getResponseById(id) {
    try {
      const docRef = doc(db, "questionnaireResponses", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error("Response not found");
      }
    } catch (error) {
      console.error("Error getting response:", error);
      throw error;
    }
  },

  /**
   * Get all questionnaire responses
   * @param {number} [maxResults=100] - Maximum number of results to return
   * @returns {Promise<Array>} - Array of all responses
   */
  async getAllResponses(maxResults = 100) {
    try {
      console.log("Getting all questionnaire responses");

      try {
        // Try with orderBy first
        const q = query(
          collection(db, "questionnaireResponses"),
          orderBy("startedAt", "desc"),
          limit(maxResults)
        );

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (indexError) {
        console.warn(
          "Index error in getAllResponses, falling back to simple query:",
          indexError.message
        );

        // If there's an index error, get all responses without sorting
        const simpleQuery = query(
          collection(db, "questionnaireResponses"),
          limit(maxResults)
        );

        const querySnapshot = await getDocs(simpleQuery);
        const results = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort in memory by startedAt descending
        return results.sort((a, b) => {
          // Handle cases where startedAt might be a Firebase timestamp or ISO string
          const getTimestamp = (doc) => {
            if (!doc.startedAt) return 0;
            if (doc.startedAt.toDate) return doc.startedAt.toDate().getTime();
            if (doc.startedAt.seconds) return doc.startedAt.seconds * 1000;
            if (typeof doc.startedAt === "string")
              return new Date(doc.startedAt).getTime();
            return 0;
          };

          return getTimestamp(b) - getTimestamp(a); // Descending order
        });
      }
    } catch (error) {
      console.error("Error getting all responses:", error);
      throw error;
    }
  },

  /**
   * Get responses by project ID
   * @param {string} projectId - The project ID
   * @returns {Promise<Array>} - Array of responses
   */
  async getResponsesByProject(projectId) {
    try {
      console.log(`Getting responses for project: ${projectId}`);

      try {
        // First, try with the complex query that requires an index
        const q = query(
          collection(db, "questionnaireResponses"),
          where("projectId", "==", projectId),
          orderBy("startedAt", "desc")
        );

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (indexError) {
        console.warn(
          "Index error in getResponsesByProject, falling back to simple query:",
          indexError.message
        );

        // If the index doesn't exist, fall back to a simpler query
        // without the orderBy clause and sort in memory
        const simpleQuery = query(
          collection(db, "questionnaireResponses"),
          where("projectId", "==", projectId)
        );

        const querySnapshot = await getDocs(simpleQuery);
        const results = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort in memory by startedAt descending
        return results.sort((a, b) => {
          // Handle cases where startedAt might be a Firebase timestamp or ISO string
          const getTimestamp = (doc) => {
            if (!doc.startedAt) return 0;
            if (doc.startedAt.toDate) return doc.startedAt.toDate().getTime();
            if (doc.startedAt.seconds) return doc.startedAt.seconds * 1000;
            if (typeof doc.startedAt === "string")
              return new Date(doc.startedAt).getTime();
            return 0;
          };

          return getTimestamp(b) - getTimestamp(a); // Descending order
        });
      }
    } catch (error) {
      console.error("Error getting responses by project:", error);
      throw error;
    }
  },

  /**
   * Get the latest response for a project
   * @param {string} projectId - The project ID
   * @returns {Promise<Object|null>} - The latest response or null if none
   */
  async getLatestResponseForProject(projectId) {
    try {
      console.log(`Getting latest response for project: ${projectId}`);
      const responses = await this.getResponsesByProject(projectId);

      if (responses.length === 0) {
        console.log(`No responses found for project: ${projectId}`);
        return null;
      }

      console.log(`Found ${responses.length} responses, returning most recent`);
      return responses[0]; // Already sorted by startedAt desc
    } catch (error) {
      console.error("Error getting latest response:", error);
      // Return null instead of throwing to prevent breaking the UI
      console.log("Returning null due to error in getLatestResponseForProject");
      return null;
    }
  },

  /**
   * Update a questionnaire response
   * @param {string} responseId - The response ID
   * @param {Object} data - The updated data
   * @returns {Promise<void>}
   */
  async updateResponse(responseId, data) {
    try {
      const docRef = doc(db, "questionnaireResponses", responseId);
      await updateDoc(docRef, data);
    } catch (error) {
      console.error("Error updating response:", error);
      throw error;
    }
  },

  /**
   * Save a response to a question
   * @param {string} responseId - The response ID
   * @param {string} questionId - The question ID
   * @param {any} value - The response value
   * @returns {Promise<void>}
   */
  async saveQuestionResponse(responseId, questionId, value) {
    try {
      const response = await this.getResponseById(responseId);
      const existingResponse = response.responses[questionId] || {};
      const responses = {
        ...response.responses,
        [questionId]: {
          ...existingResponse, // Preserve existing fields like notes, notesVisible
          questionId,
          value,
          timestamp: new Date().toISOString(),
        },
      };

      await this.updateResponse(responseId, { responses });
    } catch (error) {
      console.error("Error saving question response:", error);
      throw error;
    }
  },

  /**
   * Submit a completed questionnaire response
   * @param {string} responseId - The response ID
   * @returns {Promise<void>}
   */
  async submitResponse(responseId) {
    try {
      await this.updateResponse(responseId, {
        submittedAt: serverTimestamp(),
        status: FORM_STATUS.COMPLETED,
      });
    } catch (error) {
      console.error("Error submitting response:", error);
      throw error;
    }
  },

  /**
   * Transform response data into format suitable for brand profile
   * @param {Object} response - The questionnaire response
   * @param {Object} questionnaire - The questionnaire structure
   * @returns {Object} - Structured data for brand profile
   */
  transformResponseForBrandProfile(response, questionnaire) {
    console.log("[transformResponseForBrandProfile] Starting transformation");

    // Fallback: use snapshot if questionnaire is missing or invalid
    if (!questionnaire || !questionnaire.sections) {
      if (
        response &&
        response.questionnaireSnapshot &&
        response.questionnaireSnapshot.sections
      ) {
        console.warn(
          "[transformResponseForBrandProfile] Using questionnaireSnapshot fallback"
        );
        questionnaire = response.questionnaireSnapshot;
      } else {
        console.error(
          "[transformResponseForBrandProfile] Invalid questionnaire data and no snapshot available:",
          questionnaire
        );
        return {};
      }
    }

    if (!response || !response.responses) {
      console.error(
        "[transformResponseForBrandProfile] Invalid response data:",
        response
      );
      return {};
    }

    const result = {};

    // Map field names for brandInfo section
    // This ensures consistent field names expected by DynamicBrandInfoSection
    const brandInfoFieldMappings = {
      "What is your company name?": "Brand Name",
      "Briefly describe what your company does": "Brand Description",
      "What industry are you in?": "Tagline",
      "Company name": "Brand Name",
      "Brand tagline": "Tagline",
      "Company tagline": "Tagline",
      // Add any other potential question titles that should map to these fields
    };

    // Extract visibility settings from response metadata (if available)
    const visibilitySettings = response.metadata?.visibility || {};
    console.log(
      "[transformResponseForBrandProfile] Visibility settings:",
      visibilitySettings
    );

    // Check if optimized content should be used
    const optimizedResponses = response.metadata?.optimizedResponses || {};
    const useOptimizedInProfile =
      response.metadata?.useOptimizedInProfile || false;

    console.log(
      "[transformResponseForBrandProfile] Using optimized content:",
      useOptimizedInProfile,
      "Available optimized responses:",
      Object.keys(optimizedResponses).length
    );

    questionnaire.sections.forEach((section) => {
      // Skip sections without a type or questions
      if (!section.type || !section.questions) {
        console.warn(
          `[transformResponseForBrandProfile] Skipping section without type or questions:`,
          section
        );
        return;
      }

      // Initialize the section in the result
      result[section.type] = {};
      console.log(
        `[transformResponseForBrandProfile] Processing section: ${section.title} (${section.type})`
      );

      // Process each question in the section
      section.questions.forEach((question) => {
        // Skip questions without an ID
        if (!question.id) {
          console.warn(
            `[transformResponseForBrandProfile] Skipping question without ID:`,
            question
          );
          return;
        }

        // Check visibility settings for this question
        const questionVisibility = visibilitySettings[question.id];
        // Skip questions that are explicitly marked as not visible in profile
        if (
          questionVisibility &&
          questionVisibility.visibleInProfile === false
        ) {
          console.log(
            `[transformResponseForBrandProfile] Skipping question due to visibility settings:`,
            question.title
          );
          return;
        }

        const responseData = response.responses[question.id];
        // Check if there's optimized content for this question
        const optimizedData = optimizedResponses[question.id];
        const shouldUseOptimized = useOptimizedInProfile && optimizedData;

        if (shouldUseOptimized) {
          console.log(
            `[transformResponseForBrandProfile] Using optimized content for question: ${question.title}`
          );
        }

        // If we have response data (either original or with optimization)
        if (
          responseData &&
          responseData.value !== undefined &&
          responseData.value !== null
        ) {
          // For brandInfo section, map field names to expected keys
          if (
            section.type === "brandInfo" &&
            brandInfoFieldMappings[question.title]
          ) {
            // Even for brandInfo, we'll respect the mapped field names but use optimized content if available
            const fieldName = brandInfoFieldMappings[question.title];
            result[section.type][fieldName] = shouldUseOptimized
              ? optimizedData.displayContent
              : responseData.value;
          } else {
            // For other sections, use optimized content if available, otherwise use the original
            const baseFieldName = shouldUseOptimized
              ? optimizedData.displayTitle
              : question.title;
            const fieldValue = shouldUseOptimized
              ? optimizedData.displayContent
              : responseData.value;

            // Handle duplicate titles by using question.id to create unique keys
            // Check if a field with this title already exists
            let fieldName = baseFieldName;
            if (result[section.type][baseFieldName]) {
              // If duplicate title exists, append question ID to make it unique
              // But preserve the original title in the object for display
              fieldName = `${baseFieldName}_${question.id}`;
            }

            // If this is a scale question, include leftLabel and rightLabel if present
            if (
              question.type &&
              question.type.toLowerCase().includes("scale")
            ) {
              result[section.type][fieldName] = {
                value: fieldValue,
                leftLabel: question.leftLabel || "",
                rightLabel: question.rightLabel || "",
                notes: responseData.notes || "", // Include notes if present
                notesVisible: responseData.notesVisible !== false, // Default to true
                displayTitle: baseFieldName, // Preserve original title for display
                questionId: question.id, // Store question ID for reference
              };
            } else {
              result[section.type][fieldName] = fieldValue;
            }
          }
        }
      });
    });

    if (response.soundboard) {
      result.soundboard = response.soundboard;
    }
    if (response.deliverySections) {
      result.deliverySections = response.deliverySections;
    }

    console.log(
      "[transformResponseForBrandProfile] Final transformed data:",
      result
    );
    return result;
  },

  /**
   * Delete a questionnaire response by ID
   * @param {string} responseId - The response ID
   * @returns {Promise<void>}
   */
  async deleteResponse(responseId) {
    try {
      const docRef = doc(db, "questionnaireResponses", responseId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting questionnaire response:", error);
      throw error;
    }
  },

  /**
   * Get responses by user ID
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} - Array of responses
   */
  async getResponsesByUser(userId) {
    try {
      const q = query(
        collection(db, "questionnaireResponses"),
        where("userId", "==", userId),
        orderBy("startedAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting responses by user:", error);
      throw error;
    }
  },

  /**
   * Get the latest response for a user
   * @param {string} userId - The user ID
   * @returns {Promise<Object|null>} - The latest response or null if none
   */
  async getLatestResponseForUser(userId) {
    try {
      const responses = await this.getResponsesByUser(userId);
      if (responses.length === 0) {
        return null;
      }
      return responses[0];
    } catch (error) {
      console.error("Error getting latest response for user:", error);
      return null;
    }
  },

  /**
   * Start a new questionnaire response for a user (no projectId)
   * @param {string} questionnaireId - The questionnaire ID
   * @param {string} userId - The user ID
   * @param {Object} questionnaireSnapshot - The full questionnaire structure at the time of response creation
   * @returns {Promise<string>} - The ID of the created response
   */
  async startQuestionnaireResponseForUser(
    questionnaireId,
    userId,
    questionnaireSnapshot
  ) {
    try {
      const response = {
        questionnaireId,
        userId,
        responses: {},
        startedAt: serverTimestamp(),
        submittedAt: null,
        status: FORM_STATUS.IN_PROGRESS,
        questionnaireSnapshot: questionnaireSnapshot || null,
      };
      const docRef = await addDoc(
        collection(db, "questionnaireResponses"),
        response
      );
      return docRef.id;
    } catch (error) {
      console.error("Error starting questionnaire response for user:", error);
      throw error;
    }
  },
};

export default responseService;
